import type { GameConfig, GameConfigInput } from "./config.js";
import type {
  HazardZoneDTO,
  LobbyDTO,
  LobbySummaryDTO,
  MazeDTO,
  RosterEntry,
  RoundStanding,
  ScoreDTO,
  SpawnZoneDTO,
} from "./dtos.js";

// ---------------------------------------------------------------------------
// Client -> Server
// ---------------------------------------------------------------------------

export interface InputState {
  /** Drive forward along the tank's heading (W / Up). */
  forward: boolean;
  /** Reverse (S / Down). */
  backward: boolean;
  /** Rotate the tank's heading counter-clockwise (A / Left). */
  turnLeft: boolean;
  /** Rotate the tank's heading clockwise (D / Right). */
  turnRight: boolean;
  fire: boolean;
  /** Turret aim angle in radians (world space), pointed at the cursor. */
  aim: number;
  /** Per-player control scheme: true = 8-direction world movement (WASD =
   *  up/left/down/right), false = tank-relative (A/D rotate, W/S drive). */
  eightDir: boolean;
  /** Mobile joystick: move (and face) the full 360° toward `aim` while
   *  `forward` is held; the turret/heading both follow `aim`. Overrides the
   *  other movement schemes when set. */
  joystick: boolean;
}

export type ClientMessage =
  // First message after connecting; an existing sessionId resumes that session.
  | { type: "identify"; sessionId?: string }
  | { type: "setName"; name: string }
  | { type: "setColor"; color: string }
  | { type: "setTeam"; team: number }
  | { type: "setTeamName"; team: number; name: string }
  | { type: "setTeamColor"; team: number; color: string }
  | { type: "listLobbies" }
  | { type: "createLobby"; name: string; maxPlayers: number; config: GameConfigInput }
  | { type: "updateConfig"; maxPlayers: number; config: GameConfig }
  | { type: "joinLobby"; lobbyId: string }
  | { type: "leaveLobby" }
  | { type: "startGame" }
  // Host-only, mid-match: restart the match with the current config, or remove a player.
  | { type: "restartGame" }
  | { type: "kickPlayer"; targetId: string }
  // Host-only: hand host duties to another connected member.
  | { type: "transferHost"; targetId: string }
  | { type: "input"; input: InputState };

// ---------------------------------------------------------------------------
// Server -> Client
// ---------------------------------------------------------------------------

export type ServerMessage =
  // `sessionId` is the secret reconnect token; `resumed` is true when an
  // existing session (and its in-game progress) was restored.
  | { type: "welcome"; playerId: string; sessionId: string; resumed: boolean }
  | { type: "lobbyList"; lobbies: LobbySummaryDTO[] }
  | { type: "lobbyJoined"; lobby: LobbyDTO }
  | { type: "lobbyUpdate"; lobby: LobbyDTO }
  | { type: "lobbyClosed"; reason: string }
  // Snapshots are sent as binary frames (see shared/wire), not JSON.
  // gameStart carries the maze + roster; the first snapshot follows as binary.
  | {
      type: "gameStart";
      config: GameConfig;
      maze: MazeDTO;
      spawnZones: SpawnZoneDTO[];
      hazardZones: HazardZoneDTO[];
      roster: RosterEntry[];
      round: number;
      totalRounds: number;
      standing: RoundStanding[];
    }
  | { type: "roster"; roster: RosterEntry[] }
  // A round ended but the match continues; the next round starts after the break.
  | {
      type: "roundOver";
      round: number;
      totalRounds: number;
      winnerName: string;
      standing: RoundStanding[];
      nextInSeconds: number;
    }
  | {
      type: "gameOver";
      scores: ScoreDTO[];
      winnerName: string;
      round: number;
      totalRounds: number;
      standing: RoundStanding[];
    }
  // Periodic round-trip latency per player (id -> ms), for the scoreboard.
  | { type: "latencies"; pings: Array<{ id: string; ms: number }> }
  // This client was removed from the lobby by the host.
  | { type: "kicked"; reason: string }
  | { type: "error"; message: string };

export function encode(msg: ServerMessage | ClientMessage): string {
  return JSON.stringify(msg);
}

export function decode<T>(data: string): T {
  return JSON.parse(data) as T;
}
